import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Custom hook for real-time PLZ availability checking with debounce.
 */
export function usePLZCheck(plz) {
  const [plzCheckStatus, setPlzCheckStatus] = useState(null);
  const [plzCheckData, setPlzCheckData] = useState(null);
  const [plzMultipleOptions, setPlzMultipleOptions] = useState([]);
  const [showWaitlistForm, setShowWaitlistForm] = useState(false);

  const plzDebounceTimer = useRef(null);
  const lastCheckedPLZ = useRef('');
  const isCheckingPLZ = useRef(false);
  const checkRequestCounter = useRef(0);

  useEffect(() => {
    const trimmedPlz = plz.trim();

    if (!trimmedPlz) {
      setPlzCheckStatus(null);
      setPlzCheckData(null);
      setShowWaitlistForm(false);
      lastCheckedPLZ.current = '';
      isCheckingPLZ.current = false;
      return;
    }

    if (trimmedPlz === lastCheckedPLZ.current && plzCheckStatus && !isCheckingPLZ.current) {
      return;
    }

    if (plzDebounceTimer.current) {
      clearTimeout(plzDebounceTimer.current);
    }

    setPlzCheckStatus('checking');
    isCheckingPLZ.current = true;

    checkRequestCounter.current += 1;
    const currentRequestId = checkRequestCounter.current;

    plzDebounceTimer.current = setTimeout(async () => {
      try {
        console.log(`[Request #${currentRequestId}] Starting PLZ check for:`, trimmedPlz);
        const response = await axios.post(`${API}/availability-check`, { plz: trimmedPlz });
        const data = response.data;

        console.log(`[Request #${currentRequestId}] PLZ check result for`, trimmedPlz, ':', data);

        if (currentRequestId !== checkRequestCounter.current) {
          console.log(`[Request #${currentRequestId}] IGNORING - newer request exists (#${checkRequestCounter.current})`);
          return;
        }

        lastCheckedPLZ.current = trimmedPlz;
        isCheckingPLZ.current = false;

        if (data.status === 'multiple_choice' && data.multiple_options) {
          console.log(`[Request #${currentRequestId}] Multiple options found:`, data.multiple_options);
          setPlzMultipleOptions(data.multiple_options);
          setPlzCheckStatus('multiple_choice');
          setPlzCheckData(null);
          return;
        }

        setPlzCheckData(data);

        if (data.available) {
          console.log(`[Request #${currentRequestId}] Setting status to AVAILABLE for`, trimmedPlz);
          setPlzCheckStatus('available');
          setShowWaitlistForm(false);
        } else {
          console.log(`[Request #${currentRequestId}] Setting status to UNAVAILABLE for`, trimmedPlz);
          setPlzCheckStatus('unavailable');
          setShowWaitlistForm(true);
        }
      } catch (error) {
        console.error(`[Request #${currentRequestId}] PLZ check failed for`, trimmedPlz, ':', error);
        if (currentRequestId === checkRequestCounter.current) {
          isCheckingPLZ.current = false;
          setPlzCheckStatus('unavailable');
          setPlzCheckData(null);
        }
      }
    }, 300);

    return () => {
      if (plzDebounceTimer.current) {
        clearTimeout(plzDebounceTimer.current);
      }
    };
  }, [plz]);
  // plzCheckStatus intentionally omitted: including it would cause infinite re-renders
  // since the effect itself sets plzCheckStatus. Refs handle staleness.

  return {
    plzCheckStatus,
    plzCheckData,
    plzMultipleOptions,
    showWaitlistForm,
    setPlzMultipleOptions,
    setPlzCheckStatus,
    setPlzCheckData,
    setShowWaitlistForm,
  };
}
